import React from "react";
import { ShieldCheck, Building2, HardHat, Truck, TrendingUp, MapPinned, Home, FileCheck } from "lucide-react";

const FACTORS = [
    { key: "legal_verification",   l: "Legal Verification",   icon: ShieldCheck, max: 20 },
    { key: "builder_reputation",   l: "Builder Reputation",   icon: Building2,   max: 15 },
    { key: "construction_quality", l: "Construction Quality", icon: HardHat,     max: 10 },
    { key: "delivery_track_record",l: "Delivery Track Record",icon: Truck,       max: 10 },
    { key: "appreciation_potential",l:"Appreciation Potential",icon: TrendingUp, max: 10 },
    { key: "location_connectivity",l: "Location & Connectivity", icon: MapPinned, max: 15 },
    { key: "livability",           l: "Livability Index",     icon: Home,        max: 10 },
    { key: "documentation_status", l: "Documentation Status", icon: FileCheck,   max: 10 },
];

function scoreBadge(score) {
    if (score >= 90) return { label: "Excellent", color: "#22c55e" };
    if (score >= 80) return { label: "Very Good", color: "#84cc16" };
    if (score >= 70) return { label: "Good",       color: "#eab308" };
    if (score >= 60) return { label: "Fair",       color: "#f97316" };
    return { label: "Below Par", color: "#ef4444" };
}

export function TrustScoreBadge({ score, size = "sm" }) {
    const s = Math.round(score || 0);
    const badge = scoreBadge(s);
    const dims = size === "lg" ? "w-16 h-16 text-lg" : "w-11 h-11 text-[13px]";
    return (
        <div
            className={`${dims} rounded-full bg-black text-white flex flex-col items-center justify-center border-2 tabular-nums font-bold leading-none`}
            style={{ borderColor: badge.color }}
            data-testid="trust-score-badge"
            title={`EBG Trust Score: ${s}/100 (${badge.label})`}
        >
            <span>{s}</span>
            <span className="text-[8px] font-normal opacity-60 tracking-wider mt-0.5">TRUST</span>
        </div>
    );
}

// Radial chart for the widget
function RadialScore({ score }) {
    const s = Math.min(100, Math.max(0, score || 0));
    const badge = scoreBadge(s);
    const radius = 54;
    const circ = 2 * Math.PI * radius;
    const offset = circ - (s / 100) * circ;
    return (
        <div className="relative w-32 h-32" data-testid="trust-radial">
            <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
                <circle cx="70" cy="70" r={radius} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="9"/>
                <circle
                    cx="70" cy="70" r={radius} fill="none"
                    stroke={badge.color} strokeWidth="9" strokeLinecap="round"
                    strokeDasharray={circ} strokeDashoffset={offset}
                    style={{ transition: "stroke-dashoffset 900ms ease-out" }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-serif text-3xl tabular-nums leading-none">{s}</div>
                <div className="text-[8px] uppercase tracking-[0.24em] font-bold text-black/50 mt-1">/ 100</div>
                <div className="text-[9px] font-bold uppercase tracking-[0.2em] mt-1" style={{ color: badge.color }}>
                    {badge.label}
                </div>
            </div>
        </div>
    );
}

export default function TrustScoreWidget({ trustScore }) {
    const ts = trustScore || {};
    const total = ts.total || 0;
    return (
        <section
            data-testid="trust-score-widget"
            className="bg-white border border-black/10 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.25)] p-5 md:p-6"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">
                        EBG Trust Score
                    </div>
                    <h3 className="font-serif text-xl md:text-2xl mt-2 leading-tight">
                        An honest health-check<br/>of every listing.
                    </h3>
                    <p className="text-black/60 text-[13px] mt-2.5 max-w-md leading-relaxed">
                        Our exclusive 0–100 score assessed by our legal, valuation and delivery
                        teams. No opinion — 8 verifiable factors.
                    </p>
                </div>
                <RadialScore score={total} />
            </div>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-5 pt-5 border-t border-black/8">
                {FACTORS.map((f) => {
                    const v = ts[f.key] || 0;
                    const pct = Math.min(100, Math.round((v / f.max) * 100));
                    return (
                        <div key={f.key} data-testid={`trust-factor-${f.key}`}>
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <f.icon size={12} className="text-[var(--ebg-gold)]"/>
                                    <span className="text-[11px] text-black/75">{f.l}</span>
                                </div>
                                <span className="text-[11px] font-bold tabular-nums">{v}<span className="text-black/40">/{f.max}</span></span>
                            </div>
                            <div className="h-1 bg-black/8 relative overflow-hidden">
                                <div
                                    className="absolute inset-y-0 left-0 bg-[var(--ebg-gold)] transition-all duration-1000"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
